/**
 * BatchTrackerBar — affiche les batches de génération en cours / récemment terminés
 * en haut de la galerie. Mises à jour automatiques toutes les 5s tant qu'au moins
 * un batch contient des jobs `pending` ou `processing`.
 *
 * Props :
 *   - onJobDone  optional callback () => void  appelé quand un batch passe en is_done
 *                pour permettre au parent de refresh la galerie.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, X, AlertTriangle, Clock, Layers } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatEta = (sec) => {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)} s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  return `${(sec / 3600).toFixed(1)} h`;
};

export default function BatchTrackerBar({ onJobDone }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const prevDoneCount = useRef({}); // batch_id -> completed count

  const fetchActive = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/generation/batches/active`, { withCredentials: true });
      const next = r.data?.batches || [];
      // Détecter les batches qui ont fini de progresser → refresh galerie
      let anyProgressed = false;
      for (const b of next) {
        const prev = prevDoneCount.current[b.batch_id];
        if (prev != null && b.completed > prev) anyProgressed = true;
        prevDoneCount.current[b.batch_id] = b.completed;
      }
      setBatches(next);
      if (anyProgressed && typeof onJobDone === 'function') onJobDone();
    } catch {
      // silencieux : ne pas spammer si déconnecté
    }
  }, [onJobDone]);

  useEffect(() => {
    fetchActive();
    // Poll toutes les 5s tant qu'au moins un batch est encore actif
    const tick = setInterval(() => {
      // On polle même quand vide pour détecter un nouveau batch
      fetchActive();
    }, 5000);
    return () => clearInterval(tick);
  }, [fetchActive]);

  const handleCancel = async (batchId, pending) => {
    if (pending <= 0) return;
    if (!window.confirm(`Annuler les ${pending} génération${pending > 1 ? 's' : ''} en attente de ce batch ?`)) return;
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/generation/batches/${batchId}/cancel`,
        {},
        { withCredentials: true },
      );
      toast.success(`${r.data.cancelled} job(s) annulé(s)`);
      fetchActive();
    } catch (e) {
      toast.error('Erreur annulation');
    } finally {
      setLoading(false);
    }
  };

  if (batches.length === 0) return null;

  return (
    <div className="space-y-2 mb-3" data-testid="batch-tracker-bar">
      {batches.map((b) => {
        const done = b.completed + b.failed + b.cancelled;
        const progress = b.total > 0 ? (done / b.total) * 100 : 0;
        const isDone = b.is_done;
        const hasFailures = b.failed > 0;
        return (
          <div
            key={b.batch_id}
            className="rounded-xl p-3 backdrop-blur-md"
            style={{
              background: isDone
                ? 'linear-gradient(135deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.02) 100%)'
                : 'linear-gradient(135deg, rgba(217,160,91,0.10) 0%, rgba(217,160,91,0.02) 100%)',
              border: `1px solid ${isDone ? 'rgba(34,197,94,0.32)' : 'var(--c-border-accent, rgba(217,160,91,0.35))'}`,
            }}
            data-testid={`batch-tracker-item-${b.batch_id}`}
          >
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                  style={{
                    background: isDone ? 'rgba(34,197,94,0.18)' : 'rgba(217,160,91,0.18)',
                    border: `1px solid ${isDone ? 'rgba(34,197,94,0.32)' : 'rgba(217,160,91,0.35)'}`,
                  }}
                >
                  {isDone ? (
                    <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#86EFAC' }} />
                  ) : (
                    <Layers className="w-3.5 h-3.5 animate-pulse" style={{ color: '#FBD38D' }} />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[10px] tracking-[0.18em] uppercase font-semibold"
                      style={{ color: isDone ? '#86EFAC' : 'var(--c-gold, #D9A05B)' }}
                    >
                      Batch · {b.project_name}
                    </span>
                    <span className="text-[10px] font-mono text-white/40">{b.batch_id.replace('batch_', '#')}</span>
                  </div>
                  <p className="text-[12.5px] font-medium" style={{ color: 'var(--c-text, #E9E9EE)' }}>
                    {done}/{b.total} terminé{done > 1 ? 's' : ''}
                    {b.processing > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[11px]" style={{ color: '#FBD38D' }}>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {b.processing} en cours
                      </span>
                    )}
                    {b.pending > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-white/55">
                        <Clock className="w-3 h-3" />
                        {b.pending} en attente
                      </span>
                    )}
                    {hasFailures && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-red-300">
                        <AlertTriangle className="w-3 h-3" />
                        {b.failed} échec{b.failed > 1 ? 's' : ''}
                      </span>
                    )}
                    {b.cancelled > 0 && (
                      <span className="ml-2 text-[11px] text-white/40">
                        · {b.cancelled} annulé{b.cancelled > 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isDone && b.eta_seconds != null && b.eta_seconds > 0 && (
                  <span
                    className="text-[10.5px] font-medium px-2 py-1 rounded-md"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}
                    title="Estimation basée sur la durée moyenne des jobs terminés"
                    data-testid={`batch-eta-${b.batch_id}`}
                  >
                    ETA ~{formatEta(b.eta_seconds)}
                  </span>
                )}
                {b.pending > 0 && (
                  <button
                    type="button"
                    onClick={() => handleCancel(b.batch_id, b.pending)}
                    disabled={loading}
                    className="text-[10.5px] font-semibold px-2 py-1 rounded-md transition-colors flex items-center gap-1"
                    style={{
                      background: 'rgba(239,68,68,0.12)',
                      color: '#FCA5A5',
                      border: '1px solid rgba(239,68,68,0.3)',
                    }}
                    data-testid={`batch-cancel-${b.batch_id}`}
                    title="Annule uniquement les jobs en attente"
                  >
                    <X className="w-3 h-3" />
                    Annuler {b.pending}
                  </button>
                )}
              </div>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${progress}%`,
                  background: hasFailures
                    ? 'linear-gradient(90deg, #86EFAC 0%, #FCA5A5 100%)'
                    : (isDone
                      ? 'linear-gradient(90deg, #86EFAC 0%, #4ADE80 100%)'
                      : 'linear-gradient(90deg, #D9A05B 0%, #F4C97A 100%)'),
                }}
                data-testid={`batch-progress-${b.batch_id}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
